import Head from 'next/head';
import { PreloadImagesProps } from './props';
import { useMemo } from 'react';

/**
 * PreloadImages component that preloads images by adding preload link tags in the document head
 *
 * @param {PreloadImagesProps} props - Component props
 * @param {string[]} props.images - Array of image URLs to preload
 * @returns {JSX.Element} Head component containing preload link tags
 *
 * @example
 * // Example usage:
 * const images = [
 *   'https://example.com/image1.jpg',
 *   'https://example.com/image2.jpg'
 * ];
 * <PreloadImages images={images} />
 *
 * // Will generate in the head:
 * // <link rel="preload" href="https://example.com/image1.jpg" as="image" fetchPriority="high" />
 * // <link rel="preload" href="https://example.com/image2.jpg" as="image" fetchPriority="high" />
 */
export function PreloadImages({ images }: PreloadImagesProps) {
  // Memoize the array of link elements to prevent unnecessary re-renders
  const Images: JSX.Element[] = useMemo(
    () =>
      images.map(img => (
        <link
          rel="preload"
          href={img}
          as="image"
          fetchPriority="high"
          key={`preload-${img}`}
        />
      )),
    [images],
  );

  // Render the preload links inside the Next.js Head component
  return <Head>{Images}</Head>;
}
